<template>
  <header
    class="fixed inset-x-0 top-0 z-[60] hidden h-12 items-center gap-2 border-b border-gray-200 bg-white px-3 lg:flex dark:border-slate-800 dark:bg-slate-950">
    <!-- Logo -->
    <div class="shrink-0 pr-1">
      <a v-if="user?.is_admin" :href="adminUrl">
        <img class="h-5 w-auto dark:grayscale dark:invert" src="/public/img/rmlogo.svg" />
      </a>
      <router-link
        v-else
        :to="
          !route.params.teamPid
            ? '/'
            : {
                name: ROUTES.DASHBOARD,
                params: {
                  teamPid: route.params.teamPid
                }
              }
        ">
        <img class="h-5 w-auto" src="https://t.rightmessage.com/logo.svg" alt="Back to the Dashboard" />
      </router-link>
    </div>

    <!-- Team selector + recent pages (standard routes) -->
    <div v-if="!route.meta.fullScreen" class="flex min-w-0 items-center gap-2">
      <div class="w-56 min-w-0">
        <TeamDropdown size="small" />
      </div>
      <template v-if="showRecentPagesToggle">
        <span data-testid="recent-pages-chevron" class="shrink-0">
          <ChevronRightIcon class="h-4 w-4 text-gray-400 dark:text-slate-500" aria-hidden="true" />
        </span>
        <RecentPages />
      </template>
    </div>

    <!-- Back to listing (full-screen editors) -->
    <button
      v-else
      type="button"
      class="flex shrink-0 items-center gap-1.5 rounded-md py-1.5 pl-1.5 pr-2.5 text-[13px] font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-slate-800 dark:hover:text-white"
      @click="goBack">
      <BackwardIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
      Back
    </button>

    <button
      type="button"
      class="flex size-8 shrink-0 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-800 dark:hover:text-gray-300"
      :title="`Search (${modifierKey}+K)`"
      @click="openCommandPalette">
      <MagnifyingGlassIcon class="h-4 w-4" aria-hidden="true" />
    </button>

    <div class="min-w-0 flex-1" />

    <template v-if="!isDemoMode">
      <DataStatusIndicator />
      <div class="mx-1 h-5 w-px shrink-0 bg-gray-200 dark:bg-slate-700" aria-hidden="true" />
    </template>

    <!-- Dark-mode toggle: only on fullScreen editor routes, where the sidebar (and its toggle) is hidden -->
    <button
      v-if="route.meta.fullScreen"
      type="button"
      class="flex size-8 shrink-0 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-800 dark:hover:text-gray-300"
      :title="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
      @click="toggleDark()">
      <SunIcon v-if="!isDark" class="h-4 w-4" aria-hidden="true" />
      <MoonIcon v-else class="h-4 w-4" aria-hidden="true" />
    </button>

    <button
      v-if="!isDemoMode"
      v-tooltip.bottom="'Create a survey, quiz, funnel, or personalized page'"
      data-test="create-button"
      type="button"
      class="flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white py-1.5 pl-2 pr-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      @click="openOutcomeChooser">
      <PlusIcon class="h-4 w-4 shrink-0 text-gray-400 dark:text-slate-400" aria-hidden="true" />
      Create
    </button>

    <button
      v-if="hasBackups"
      v-tooltip.bottom="'View and restore config backups'"
      type="button"
      class="flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white py-1.5 pl-2 pr-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      @click="versionDrawerVisible = true">
      <ClockIcon class="h-4 w-4 shrink-0 text-gray-400 dark:text-slate-400" aria-hidden="true" />
      Backups
    </button>
    <span
      v-tooltip.bottom="
        previewDisabled ? 'Preview requires your tracking snippet to be installed on your website' : undefined
      "
      class="shrink-0">
      <div class="relative shrink-0">
        <div class="inline-flex">
          <button
            data-test="preview-primary-button"
            type="button"
            :disabled="previewDisabled"
            class="flex items-center gap-1.5 rounded-l-md rounded-r-none border border-gray-300 bg-white py-1.5 pl-2 pr-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:disabled:hover:bg-slate-900"
            @click="openActivePreview">
            <EyeIcon class="h-4 w-4 shrink-0 text-gray-400 dark:text-slate-400" aria-hidden="true" />
            Preview
          </button>
          <button
            data-test="preview-menu-button"
            type="button"
            :disabled="previewDisabled"
            class="-ml-px flex items-center rounded-l-none rounded-r-md border border-gray-300 bg-white px-2 py-1.5 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:disabled:hover:bg-slate-900"
            @click="togglePreviewMenu">
            <ChevronDownIcon class="h-4 w-4 shrink-0 text-gray-400 dark:text-slate-400" aria-hidden="true" />
          </button>
        </div>

        <Transition
          enter-active-class="transition duration-100 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition duration-75 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95">
          <div
            v-if="previewMenuOpen"
            data-test="preview-menu"
            class="absolute right-0 top-full z-50 mt-1 w-80 origin-top-right rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800">
            <div
              class="px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              Preview on
            </div>

            <div
              v-if="previewUrls.isLoading.value && previewUrls.hitlist.value.length === 0"
              class="px-2.5 py-2 text-sm text-gray-500 dark:text-gray-400">
              Loading pages…
            </div>

            <template v-for="entry in previewUrls.hitlist.value" :key="entry.url">
              <button
                type="button"
                class="flex w-full items-start gap-x-2.5 px-2.5 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-slate-700/50"
                @click="openPreviewEntry(entry.url, entry.source)">
                <GlobeAltIcon class="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                <div class="min-w-0 flex-1">
                  <div class="truncate font-medium">{{ getPreviewUrlLabel(entry.url) }}</div>
                  <div class="text-[11px] text-gray-400 dark:text-gray-500">
                    {{
                      entry.source === 'custom'
                        ? 'Custom URL'
                        : `${entry.uniqueVisitors} visitors · ${entry.events} events`
                    }}
                  </div>
                </div>
              </button>
            </template>

            <div
              v-if="!previewUrls.isLoading.value && previewUrls.hitlist.value.length === 0"
              class="px-2.5 py-2 text-sm text-gray-500 dark:text-gray-400">
              No tracked pages yet
            </div>

            <div class="my-1 border-t border-gray-200 dark:border-slate-700" />

            <button
              data-test="preview-enter-url-button"
              type="button"
              class="flex w-full items-center gap-x-2.5 px-2.5 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-slate-700/50"
              @click="openCustomPreviewDialog">
              <GlobeAltIcon class="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
              <div class="font-medium">Enter URL</div>
            </button>
          </div>
        </Transition>
      </div>
    </span>
    <PublishButton />

    <ConfigVersionDrawer :visible="versionDrawerVisible" @update:visible="versionDrawerVisible = $event" />
  </header>
  <Dialog
    v-model:visible="customPreviewDialogVisible"
    modal
    header="Enter preview URL"
    :style="{ width: '32rem', maxWidth: 'calc(100vw - 2rem)' }"
    @hide="resetCustomPreviewDialog">
    <form class="space-y-4" @submit.prevent="submitCustomPreviewUrl">
      <div class="space-y-2">
        <label for="topbar-preview-url" class="block text-sm font-medium text-gray-900 dark:text-slate-100">URL</label>
        <input
          id="topbar-preview-url"
          v-model="customPreviewUrl"
          data-test="preview-custom-url-input"
          type="url"
          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          :class="customPreviewUrlError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''"
          autocomplete="off"
          @blur="handleCustomPreviewUrlBlur" />
        <p class="text-xs text-gray-500 dark:text-slate-400">
          Enter the full page URL you want to preview, including http:// or https://
        </p>
        <p
          v-if="customPreviewUrlError"
          data-test="preview-custom-url-error"
          class="text-sm text-red-600 dark:text-red-400">
          {{ customPreviewUrlError }}
        </p>
      </div>
      <div class="flex justify-end gap-2">
        <button
          type="button"
          class="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
          @click="closeCustomPreviewDialog">
          Cancel
        </button>
        <button
          data-test="preview-custom-url-submit"
          type="submit"
          class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500">
          Open Preview
        </button>
      </div>
    </form>
  </Dialog>
</template>

<script setup lang="ts">
import {
  ArrowLeftIcon as BackwardIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  EyeIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  MoonIcon,
  PlusIcon,
  SunIcon
} from '@heroicons/vue/24/outline'
import { ROUTES } from '@/router/routeNames'
import { redirectUrlSchema } from '@/formValidationschemas'
import ConfigVersionDrawer from '@/components/common/ConfigVersionDrawer.vue'
import { configVersionService } from '@/services/configVersionService'

const route = useRoute()
const router = useRouter()

const isDark = useDark()
const toggleDark = useToggle(isDark)
const isSettingsMode = computed(() => !!route.meta?.settingsMode)
const isProfileMode = computed(() => !!route.meta?.profileMode)
const isConfigMode = computed(() => isSettingsMode.value || isProfileMode.value)
const { recentPages, currentPage } = useRecentPages()
const showRecentPagesToggle = computed(
  () => !route.meta.fullScreen && !isConfigMode.value && Boolean(currentPage.value || recentPages.value.length)
)

const authStore = useAuthStore()
const { user } = storeToRefs(authStore)
const configStore = useConfigStore()
const { owner, currentTeam, currentTeamPid, lastChecksum } = storeToRefs(configStore)

const isDemo = inject('isDemo', false)
const isDemoMode = computed(() => unref(isDemo))
const previewUrls = usePreviewUrls({ teamPid: currentTeamPid, isDemoMode })

const reportStore = useReportStore()
const { dataLastReceived, isLoadingDataLastReceived, isErrorDataLastReceived } = storeToRefs(reportStore)

// Backups button only appears once at least one config version exists
const versionDrawerVisible = ref(false)
const hasBackups = ref(false)

watch(
  [currentTeamPid, lastChecksum],
  async ([pid], [prevPid]) => {
    // Reset only when the team actually changes. lastChecksum is in the
    // dependency list so the first save of a fresh project flips the button on,
    // but it changes on every ~2s autosave — resetting to false here would make
    // the button flicker out/in on every save and fire a redundant request each
    // time. Once a team has backups they don't disappear mid-session, so we
    // stop querying as soon as we've seen one.
    if (pid !== prevPid) {
      hasBackups.value = false
    }
    if (!pid || isDemoMode.value || hasBackups.value) return
    try {
      const { total } = await configVersionService.listVersions(pid, 0, 1)
      hasBackups.value = total > 0
    } catch {
      // Leave hasBackups as-is on a transient error; a pid change already
      // reset it to false, and a same-team recheck only runs while it's false.
    }
  },
  { immediate: true }
)

// Preview loads the user's website — pointless until the snippet is installed
// and has reported at least one event
const previewDisabled = computed(() => {
  if (isDemoMode.value) return false
  if (isLoadingDataLastReceived.value) return false
  // A reporting query error is not evidence the snippet is missing — keep preview usable
  if (isErrorDataLastReceived.value) return false
  return !dataLastReceived.value?.last_update
})

const previewMenuOpen = ref(false)
const customPreviewDialogVisible = ref(false)
const customPreviewUrl = ref('')
const customPreviewUrlError = ref('')
watch(
  [currentTeamPid, previewDisabled],
  ([teamPid, disabled]) => {
    previewMenuOpen.value = false
    if (isDemoMode.value || !teamPid || disabled) {
      return
    }

    void previewUrls.loadSuggestions()
  },
  { immediate: true }
)

// Command palette (Cmd+K / Ctrl+K)
const { open: openCommandPalette } = useCommandPalette()

// Global outcome-based creation — dialog itself is mounted in AuthenticatedLayout
const { open: openOutcomeChooserDialog } = useOutcomeChooser()
const openOutcomeChooser = () => openOutcomeChooserDialog('top_bar')

// OS detection for keyboard shortcut display
const isMac = computed(() => {
  if (typeof navigator === 'undefined') return false
  return /Mac|iPod|iPhone|iPad/.test(navigator.platform)
})
const modifierKey = computed(() => (isMac.value ? '⌘' : 'Ctrl'))

const goBack = () => {
  router.push({
    name: (route.meta.backButton as string) || ROUTES.OFFERFUNNELS
  })
}

const getPreviewUrlLabel = (url: string) => {
  try {
    const parsedUrl = new URL(url)
    return `${parsedUrl.hostname}${parsedUrl.pathname}`
  } catch {
    return url
  }
}

const customPreviewDefaultUrl = computed(() => {
  const website = currentTeam.value?.website?.trim()
  if (!website) {
    return ''
  }

  const normalizedUrl = normalizePreviewUrl(website)
  if (!normalizedUrl) {
    return ''
  }

  return new URL(normalizedUrl).origin
})

function resetCustomPreviewDialog() {
  customPreviewUrl.value = ''
  customPreviewUrlError.value = ''
}

function closeCustomPreviewDialog() {
  customPreviewDialogVisible.value = false
  resetCustomPreviewDialog()
}

function validateCustomPreviewUrl(value: string) {
  const trimmedValue = value.trim()
  const result = redirectUrlSchema.safeParse(trimmedValue)

  if (!result.success) {
    customPreviewUrlError.value = result.error.issues[0]?.message || 'Invalid URL'
    return null
  }

  const normalizedUrl = normalizePreviewUrl(trimmedValue)
  if (!normalizedUrl) {
    customPreviewUrlError.value = 'Enter a full http(s) URL to preview on your site.'
    return null
  }

  customPreviewUrlError.value = ''
  return normalizedUrl
}

function handleCustomPreviewUrlBlur() {
  validateCustomPreviewUrl(customPreviewUrl.value)
}

watch(customPreviewUrl, (value) => {
  if (customPreviewUrlError.value) {
    validateCustomPreviewUrl(value)
  }
})

function openCustomPreviewDialog() {
  previewMenuOpen.value = false
  customPreviewUrl.value = customPreviewDefaultUrl.value
  customPreviewUrlError.value = ''
  customPreviewDialogVisible.value = true
}

function submitCustomPreviewUrl() {
  const normalizedUrl = validateCustomPreviewUrl(customPreviewUrl.value)
  if (!normalizedUrl) {
    return
  }

  previewUrls.openPreviewUrl(normalizedUrl, 'custom')
  closeCustomPreviewDialog()
}

const openActivePreview = async () => {
  previewMenuOpen.value = false
  if (!previewUrls.activeUrl.value) {
    await previewUrls.loadSuggestions()
  }

  if (!previewUrls.activeUrl.value) {
    openCustomPreviewDialog()
    return
  }

  previewUrls.openActivePreview()
}

const openPreviewEntry = (url: string, source: 'suggested' | 'custom') => {
  previewMenuOpen.value = false
  previewUrls.openPreviewUrl(url, source)
}

const togglePreviewMenu = async () => {
  if (previewDisabled.value) {
    return
  }

  if (previewMenuOpen.value) {
    previewMenuOpen.value = false
    return
  }

  await previewUrls.loadSuggestions()
  previewMenuOpen.value = true
}

const adminUrl = computed(() => {
  const backendUrl = import.meta.env.VITE_APP_BACKEND_HOST + '/manage'

  if (owner.value.email !== user.value?.email) {
    return backendUrl + `/users/${owner.value.id}`
  }

  return backendUrl
})
</script>
